// Seller details printed on invoices (GET /settings/public/store). The TRN is
// null until the owner saves a valid 15-digit number in Admin → Settings;
// invoices then leave the TRN line out instead of printing a fake one.
// The same answer carries the shop facts `vat_registered` / `vat_rate` (what
// POST /orders charges: 0 while the store is not VAT-registered) and
// `return_window_days` (what POST /returns enforces).

import { API_URL, memoPublic } from './api';
import { FACTS, readFacts, writeFacts } from './factsCache';
import { DEFAULT_VAT_RATE, normalizeVatRate } from './vat';

// Mirrors backend routes/returns.RETURN_WINDOW_DAYS until the server answers.
export const DEFAULT_RETURN_WINDOW_DAYS = 14;

const positiveInt = (value, fallback) => {
  const n = Number(value);
  return value !== null && value !== undefined && Number.isInteger(n) && n > 0 ? n : fallback;
};

const text = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const RETURN_FEES = new Set(['free', 'customer']);
const httpsList = (value) => (Array.isArray(value) ? value : [])
  .filter((u) => typeof u === 'string' && /^https:\/\/[^\s"<>]+$/.test(u));

/**
 * `vatRegistered` / `vatRate` from the answer. Registered stores charge
 * `vat_rate`; a store that is not registered charges 0 and the shop never
 * mentions VAT. An answer without `vat_registered` (older server) counts as
 * registered when its rate is above 0. `vatKnown` is false when the answer
 * said nothing about VAT (no answer, failed read): the shop then keeps what
 * it already shows instead of switching prices.
 */
const vatFacts = (d) => {
  const known = typeof d.vat_registered === 'boolean' || (d.vat_rate !== undefined && d.vat_rate !== null);
  const rate = normalizeVatRate(d.vat_rate);
  const registered = typeof d.vat_registered === 'boolean' ? d.vat_registered : rate > 0;
  return { vatRegistered: registered, vatRate: registered ? rate : DEFAULT_VAT_RATE, vatKnown: known };
};

export const normalizeStoreDetails = (data) => {
  const d = data && typeof data === 'object' ? data : {};
  const trn = text(d.store_trn);
  const year = Number(d.founded_year);
  return {
    name: text(d.store_name) || 'ANUM 360 GALLERY',
    trn: trn && /^\d{15}$/.test(trn) ? trn : null,
    address: text(d.store_address),
    phone: text(d.store_phone),
    email: text(d.store_email),
    ...vatFacts(d),
    returnWindowDays: positiveInt(d.return_window_days, DEFAULT_RETURN_WINDOW_DAYS),
    // The store as an entity (Organization JSON-LD, About page): Admin → Settings.
    foundedYear: Number.isInteger(year) && year >= 1900 && year <= 2100 ? year : null,
    city: text(d.city),
    sameAs: httpsList(d.same_as),
    returnFees: RETURN_FEES.has(d.return_fees) ? d.return_fees : null,
  };
};

const requestStoreDetails = async (fetchImpl, remember = false) => {
  const response = await fetchImpl(`${API_URL}/settings/public/store`);
  if (!response.ok) throw new Error(`store details ${response.status}`);
  const data = await response.json();
  if (remember) writeFacts(FACTS.store, data);
  return normalizeStoreDetails(data);
};

/** The last answer (≤ 10 min old, utils/factsCache) for a first paint, or null. */
export const cachedStoreDetails = () => {
  const data = readFacts(FACTS.store);
  return data && typeof data === 'object' ? normalizeStoreDetails(data) : null;
};

// Without `fetchImpl` the answer is shared (utils/api memoPublic): the facts
// provider, the invoice and the structured data ask once per minute, not
// once each. A failure is not kept; the defaults apply until the next ask.
export const fetchStoreDetails = async (fetchImpl) => {
  try {
    if (fetchImpl) return await requestStoreDetails(fetchImpl);
    return await memoPublic('facts:store', () => requestStoreDetails(fetch, true));
  } catch {
    return normalizeStoreDetails(null);
  }
};
